import { useCallback, useEffect, useState } from 'react'

import { api } from '../api'
import { t } from '../i18n'
import { haptic, notify, useBackButton } from '../telegram'
import type { AdminCoach } from '../types'
import { CoachPhoto, LANG_NAMES, coachPrice } from './Coaches'

interface Props {
  onClose: () => void
}

/** Администратор: анкеты тренеров (на проверке — сверху) и спрос на каждого. */
export default function AdminCoaches({ onClose }: Props) {
  const [items, setItems] = useState<AdminCoach[] | null>(null)
  const [open, setOpen] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => useBackButton(onClose), [onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setItems(await api.adminCoaches())
    } catch (e) {
      setItems([])
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function act(id: number, action: 'approve' | 'reject' | 'pause') {
    setBusy(true)
    haptic('medium')
    try {
      await api.adminReviewCoach(id, action)
      await load()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().adminCoaches}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={onClose} aria-label={t().back}>
          ←
        </button>
        <div className="title chat-title">🎓 {t().adminCoaches}</div>
      </div>
      <div className="chat-list">
        {items === null && <p className="muted">{t().loading}</p>}
        {items?.length === 0 && <div className="card muted">{t().coachesEmpty}</div>}
        {items?.map((c) => (
          <div key={c.id} className="card">
            <button className="coach-row bare" onClick={() => setOpen(open === c.id ? null : c.id)}>
              <CoachPhoto coach={c} size={44} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <b className="ellipsis">{c.display_name}</b>
                <span className="muted small ellipsis">
                  {c.player_name} · {t().adminCoachStats(c.requests, c.accepted)}
                </span>
              </span>
              <span className={`coach-status ${c.status}`}>{t().coachStatus[c.status].split(' — ')[0]}</span>
            </button>
            {open === c.id && (
              <div style={{ marginTop: 10 }}>
                {c.certificate && <div className="muted">{c.certificate}</div>}
                <div style={{ whiteSpace: 'pre-line', margin: '6px 0' }}>{c.bio}</div>
                <div className="muted small">
                  {c.languages.map((l) => LANG_NAMES[l] ?? l).join(', ')} ·{' '}
                  {t().coachLevels(c.level_min.toFixed(1), c.level_max.toFixed(1))}
                </div>
                <div className="muted small">{c.clubs.map((club) => `${club.name} (${club.city})`).join(', ')}</div>
                <div className="muted small">
                  {c.formats
                    .map((f) => `${t().fmtName[f]}${coachPrice(c, f) != null ? ` ${coachPrice(c, f)} €` : ''}`)
                    .join(' · ')}
                </div>
                <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {c.status !== 'approved' && (
                    <button style={{ flex: 1 }} disabled={busy} onClick={() => act(c.id, 'approve')}>
                      {c.status === 'paused' ? t().adminRestore : t().adminApprove}
                    </button>
                  )}
                  {c.status === 'pending' && (
                    <button className="ghost" style={{ flex: 1 }} disabled={busy} onClick={() => act(c.id, 'reject')}>
                      {t().adminReject}
                    </button>
                  )}
                  {c.status === 'approved' && (
                    <button className="ghost" style={{ flex: 1 }} disabled={busy} onClick={() => act(c.id, 'pause')}>
                      {t().adminPause}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
